/**
 * AllBaise Home — Profile-first layout with Business Card + Wishlist + Activity
 * Top section: Business Card (left+center) | Wishlist Menu (right)
 * Below: Corporate+People / Activity Feed / Deals+Wishes
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  Loader2, Heart, MessageCircle, MessageSquare, Send, MapPin, Users,
  Share2, Edit3, Image,
  Building2, Briefcase, Star, Gift, Search, Filter, GraduationCap,
  Award, Phone, Mail, Globe, Tag, Package,
  Camera, UserPlus, Building, Check, ChevronDown, ChevronUp,
  ExternalLink, ShoppingBag, Target,
} from "lucide-react";
import TopNav from "@/components/TopNav";
import GeoFilterBar from "@/components/GeoFilterBar";

const API = (() => {
  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1") return "http://localhost:8001/api";
  return `https://api.${host}/api`;
})();

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const dummyAvatar = (name, id) => {
  const gradients = [
    "from-violet-500 to-purple-600", "from-emerald-500 to-teal-600",
    "from-blue-500 to-cyan-600", "from-rose-500 to-pink-600",
    "from-amber-500 to-orange-600",
  ];
  const h = (id || name || "").length;
  const bg = gradients[h % gradients.length];
  return { bg, letter: (name || "?")[0].toUpperCase() };
};

// ── Filter Bar ──
function FilterBar({ search, onSearchChange, location, onLocationChange, placeholder, extraFilters }) {
  return (
    <div className="flex flex-col gap-2 mb-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
          <input type="text" value={search} onChange={(e) => onSearchChange(e.target.value)}
            placeholder={placeholder || "Search..."}
            className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
        </div>
        {location !== undefined && (
          <div className="relative flex-1">
            <MapPin className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
            <input type="text" value={location} onChange={(e) => onLocationChange(e.target.value)}
              placeholder="Location..."
              className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
          </div>
        )}
      </div>
      {extraFilters}
    </div>
  );
}

// ── Corporate Hub Sidebar ──
function CorporateSidebar({ onNavigate }) {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [location, setLocation] = useState("");
  const [csCountry, setCsCountry] = useState("all");
  const [csRegion, setCsRegion] = useState("all");
  const [csRadius, setCsRadius] = useState("");
  const [csOrgType, setCsOrgType] = useState("all");
  const [csCategory, setCsCategory] = useState("all");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/v2/orgs`, { headers: getHeaders() })
      .then(res => setOrgs(res.data.items || []))
      .catch(() => setOrgs([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = orgs.filter(o => {
    if (search && !(o.name || "").toLowerCase().includes(search.toLowerCase()) && !(o.description || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (location && !(o.country || o.location || "").toLowerCase().includes(location.toLowerCase())) return false;
    if (csCountry !== "all" && o.country !== csCountry) return false;
    if (csOrgType !== "all" && o.org_type !== csOrgType) return false;
    if (csCategory !== "all" && o.category !== csCategory) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5 text-violet-400" /> Corporate Hub
        </h3>
        <button onClick={() => onNavigate("/corporate-hub")} className="text-xs text-violet-400 hover:text-violet-300 font-medium">View All</button>
      </div>
      <GeoFilterBar country={csCountry} onCountryChange={setCsCountry} region={csRegion} onRegionChange={setCsRegion} radius={csRadius} onRadiusChange={setCsRadius} />
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsOrgType("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        <button key="government" onClick={() => setCsOrgType("government")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "government" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>government</button>
        <button key="ngo" onClick={() => setCsOrgType("ngo")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "ngo" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>ngo</button>
        <button key="business" onClick={() => setCsOrgType("business")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csOrgType === "business" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>business</button>
      </div>
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setCsCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "all" ? "bg-violet-600/30 text-violet-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        <button key="National" onClick={() => setCsCategory("National")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "National" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>National</button>
        <button key="Provincial" onClick={() => setCsCategory("Provincial")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Provincial" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Provincial</button>
        <button key="Municipal" onClick={() => setCsCategory("Municipal")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Municipal" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Municipal</button>
        <button key="State-Owned Entity" onClick={() => setCsCategory("State-Owned Entity")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "State-Owned Entity" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>State-Owned Entity</button>
        <button key="Regulatory" onClick={() => setCsCategory("Regulatory")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Regulatory" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Regulatory</button>
        <button key="Human Rights" onClick={() => setCsCategory("Human Rights")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Human Rights" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Human Rights</button>
        <button key="Education" onClick={() => setCsCategory("Education")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Education" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Education</button>
        <button key="Health" onClick={() => setCsCategory("Health")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Health" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Health</button>
        <button key="Environment" onClick={() => setCsCategory("Environment")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Environment" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Environment</button>
        <button key="Community" onClick={() => setCsCategory("Community")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Community" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Community</button>
        <button key="Relief" onClick={() => setCsCategory("Relief")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Relief" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Relief</button>
        <button key="Technology" onClick={() => setCsCategory("Technology")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Technology" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Technology</button>
        <button key="Finance" onClick={() => setCsCategory("Finance")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Finance" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Finance</button>
        <button key="Manufacturing" onClick={() => setCsCategory("Manufacturing")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Manufacturing" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Manufacturing</button>
        <button key="Retail" onClick={() => setCsCategory("Retail")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Retail" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Retail</button>
        <button key="Healthcare" onClick={() => setCsCategory("Healthcare")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Healthcare" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Healthcare</button>
        <button key="Energy" onClick={() => setCsCategory("Energy")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Energy" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Energy</button>
        <button key="Agriculture" onClick={() => setCsCategory("Agriculture")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Agriculture" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Agriculture</button>
        <button key="Logistics" onClick={() => setCsCategory("Logistics")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Logistics" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Logistics</button>
        <button key="Tourism" onClick={() => setCsCategory("Tourism")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${csCategory === "Tourism" ? "bg-violet-600/30 text-violet-300" : "bg-surface-item text-app-secondary"}`}>Tourism</button>
      </div>
      <FilterBar search={search} onSearchChange={setSearch} location={location} onLocationChange={setLocation} />
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-app-secondary text-center py-4">No organizations found</p>
        ) : (
          filtered.slice(0, 8).map(org => (
            <div key={org.id || org._id} onClick={() => onNavigate(`/corporate-hub/${org.id || org._id}`)}
              className="bg-surface-item rounded-lg p-2.5 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${dummyAvatar(org.name, org.id || org._id).bg} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                  {dummyAvatar(org.name, org.id || org._id).letter}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-app">{org.name}</p>
                  <p className="text-[10px] text-app-secondary truncate">{org.description || org.org_type || ""}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {org.country && <span className="text-[10px] text-app-secondary">{org.country}</span>}
                    {org.verified && <Badge className="bg-emerald-600/20 text-emerald-400 text-[8px] px-1 py-0">Verified</Badge>}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── People Sidebar ──
function PeopleSidebar({ onNavigate }) {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [skill, setSkill] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/v2/people`, { headers: getHeaders() })
      .then(res => setPeople(res.data.items || []))
      .catch(() => setPeople([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = people.filter(p => {
    if (search && !(p.name || "").toLowerCase().includes(search.toLowerCase()) && !(p.username || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (skill) {
      const skills = p.skills || p.expertise || [];
      const skillList = Array.isArray(skills) ? skills : [skills];
      if (!skillList.some(s => (s || "").toLowerCase().includes(skill.toLowerCase()))) return false;
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2 mt-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-emerald-400" /> My People
        </h3>
        <button onClick={() => onNavigate("/people-directory")} className="text-xs text-emerald-400 hover:text-emerald-300 font-medium">View All</button>
      </div>
      <FilterBar search={search} onSearchChange={setSearch}
        placeholder="Search people..."
        location={undefined} onLocationChange={() => {}}
        extraFilters={
          <div className="relative flex-1">
            <Award className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
            <input type="text" value={skill} onChange={(e) => setSkill(e.target.value)}
              placeholder="Skill..." className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-emerald-500" />
          </div>
        } />
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-app-secondary text-center py-4">No people found</p>
        ) : (
          filtered.slice(0, 8).map(person => (
            <div key={person.user_id || person._id} onClick={() => onNavigate(`/people/${person.user_id}`)}
              className="bg-surface-item rounded-lg p-2.5 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${dummyAvatar(person.name, person.user_id).bg} flex items-center justify-center text-white text-xs font-bold shrink-0`}>
                  {dummyAvatar(person.name, person.user_id).letter}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-app">{person.name}</p>
                  <p className="text-[10px] text-app-secondary">@{person.username || "user"}</p>
                  {person.position && <p className="text-[10px] text-app-secondary">{person.position}</p>}
                  {person.skills && Array.isArray(person.skills) && person.skills.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {person.skills.slice(0, 2).map((s, i) => (
                        <span key={i} className="text-[8px] bg-violet-600/20 text-violet-400 px-1.5 py-0.5 rounded">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}


// ── Deals Sidebar ──
function DealsSidebar({ onNavigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dsCategory, setDsCategory] = useState("all");
  const [dCountry, setDCountry] = useState("all");
  const [dRegion, setDRegion] = useState("all");
  const [dRadius, setDRadius] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/deals/list`, { headers: getHeaders() })
      .then(res => setDeals((res.data.items || []).filter(d => d.status === "active")))
      .catch(() => setDeals([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = deals.filter(d => {
    if (search && !d.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (dsCategory !== "all" && d.category !== dsCategory) return false;
    if (dCountry !== "all" && d.country !== dCountry) return false;
    return d.status === "active";
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5 text-amber-400" /> Deals
        </h3>
        <button onClick={() => onNavigate("/deals")} className="text-xs text-amber-400 hover:text-amber-300 font-medium">View All</button>
      </div>
      <GeoFilterBar country={dCountry} onCountryChange={setDCountry} region={dRegion} onRegionChange={setDRegion} radius={dRadius} onRadiusChange={setDRadius} />
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setDsCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dsCategory === "all" ? "bg-amber-600/30 text-amber-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["Food & Drink","Health & Beauty","Travel & Hotels","Electronics","Fashion","Home & Garden","Automotive","Education","Services","Entertainment","Groceries","Other"].map(c => (
          <button key={c} onClick={() => setDsCategory(c)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${dsCategory === c ? "bg-amber-600/30 text-amber-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>{c}</button>
        ))}
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search deals..."
          className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-amber-500" />
      </div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-app-secondary text-center py-4">No active deals</p>
        ) : (
          filtered.slice(0, 6).map(deal => (
            <div key={deal.id} onClick={() => onNavigate("/deals")}
              className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-600 to-orange-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  <Tag className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-app">{deal.title}</p>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-amber-400">R{deal.deal_price}</span>
                    <span className="text-[10px] text-app-secondary line-through">R{deal.original_price}</span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
// ── Wishes Sidebar ──
function WishesSidebar({ onNavigate }) {
  const [wishes, setWishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [wsCategory, setWsCategory] = useState("all");
  const [wCountry, setWCountry] = useState("all");
  const [wRegion, setWRegion] = useState("all");
  const [wRadius, setWRadius] = useState("");

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/wishes/list`, { headers: getHeaders() })
      .then(res => setWishes(res.data.items || []))
      .catch(() => setWishes([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = wishes.filter(w => {
    if (search && !w.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (wsCategory !== "all" && w.category !== wsCategory) return false;
    if (wCountry !== "all" && w.country !== wCountry) return false;
    return true;
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Star className="w-3.5 h-3.5 text-pink-400" /> Wishes
        </h3>
        <button onClick={() => onNavigate("/wish-centre")} className="text-xs text-pink-400 hover:text-pink-300 font-medium">View All</button>
      </div>
      <GeoFilterBar country={wCountry} onCountryChange={setWCountry} region={wRegion} onRegionChange={setWRegion} radius={wRadius} onRadiusChange={setWRadius} />
      <div className="flex gap-1 mb-1 overflow-x-auto scrollbar-dark">
        <button onClick={() => setWsCategory("all")} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wsCategory === "all" ? "bg-pink-600/30 text-pink-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>All</button>
        {["Services","Products","Jobs","Learning","Health","Travel","Technology","Home","Fashion","Food","Entertainment","Volunteering","Mentorship","Funding","Other"].map(c => (
          <button key={c} onClick={() => setWsCategory(c)} className={`text-xs px-3 py-1 rounded-full whitespace-nowrap ${wsCategory === c ? "bg-pink-600/30 text-pink-300 font-semibold" : "bg-surface-item text-app-secondary"}`}>{c}</button>
        ))}
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-app-secondary" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search wishes..."
          className="w-full bg-surface-item border border-input rounded-lg pl-8 pr-3 py-1.5 text-xs text-app placeholder:text-app-secondary focus:outline-none focus:border-pink-500" />
      </div>
      <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
        {loading ? (<div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
        ) : filtered.length === 0 ? (<p className="text-xs text-app-secondary text-center py-4">No wishes yet</p>
        ) : (
          filtered.slice(0, 6).map(wish => (
            <div key={wish.id} onClick={() => onNavigate("/wish-centre")}
              className="bg-surface-item rounded-lg p-2 cursor-pointer hover:bg-surface-hover transition-colors">
              <div className="flex items-start gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                  <Star className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate text-app">{wish.title}</p>
                  <p className="text-[11px] text-app-secondary truncate">{wish.category}{wish.location ? ` • ${wish.location}` : ""}</p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
// ── WISHLIST MENU (Right sidebar) ──
function WishlistMenu({ onNavigate, isOwner }) {
  const [wishData, setWishData] = useState([]);
  const [expanded, setExpanded] = useState(null); // 'orgs' | 'deals' | 'people' | 'jobs'
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ type: "", title: "", description: "" });

  useEffect(() => {
    fetchProfileWishes();
  }, []);

  const fetchProfileWishes = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/user/profile-wishes`, { headers: getHeaders() });
      setWishData(res.data.items || []);
    } catch { setWishData([]); }
    setLoading(false);
  };

  const addWish = async () => {
    if (!editForm.title || !editForm.type) { toast.error("Title and type required"); return; }
    const newItems = [...wishData, { wish_type: editForm.type, title: editForm.title, description: editForm.description }];
    try {
      await axios.put(`${API}/user/profile-wishes`, { wishes: newItems }, { headers: getHeaders() });
      toast.success("Wish added!");
      setEditForm({ type: "", title: "", description: "" });
      fetchProfileWishes();
    } catch { toast.error("Failed to add"); }
  };

  const removeWish = async (itemToRemove) => {
    const idx = wishData.findIndex(w => w.title === itemToRemove.title && w.wish_type === itemToRemove.wish_type);
    if (idx === -1) return;
    const newItems = wishData.filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/user/profile-wishes`, { wishes: newItems }, { headers: getHeaders() });
      toast.success("Wish removed");
      fetchProfileWishes();
    } catch { toast.error("Failed to remove"); }
  };

  // Group wishes by type
  const orgWishes = wishData.filter(w => w.wish_type === "org").slice(0, 3);
  const dealWishes = wishData.filter(w => w.wish_type === "deal").slice(0, 3);
  const peopleWishes = wishData.filter(w => w.wish_type === "people").slice(0, 3);
  const jobWishes = wishData.filter(w => w.wish_type === "job").slice(0, 3);

  const sections = [
    { key: "orgs", label: "Organisations to Connect", icon: Building, color: "from-violet-500 to-purple-600", bg: "bg-violet-600/20", text: "text-violet-300", items: orgWishes },
    { key: "deals", label: "Deals Wishlist", icon: ShoppingBag, color: "from-amber-500 to-orange-600", bg: "bg-amber-600/20", text: "text-amber-300", items: dealWishes },
    { key: "people", label: "People to Connect", icon: Users, color: "from-emerald-500 to-teal-600", bg: "bg-emerald-600/20", text: "text-emerald-300", items: peopleWishes },
    { key: "jobs", label: "Job Opportunities", icon: Briefcase, color: "from-blue-500 to-cyan-600", bg: "bg-blue-600/20", text: "text-blue-300", items: jobWishes },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5">
          <Target className="w-3.5 h-3.5 text-rose-400" /> My Wishlist
        </h3>
        {isOwner && (
          <button onClick={() => setEditing(!editing)} className={`text-[9px] px-2 py-0.5 rounded ${editing ? "bg-rose-600/30 text-rose-300" : "bg-zinc-700/50 text-app-secondary hover:text-app"}`}>
            {editing ? "Done" : "Edit"}
          </button>
        )}
      </div>
      {editing && (
        <div className="mb-3 p-2 bg-surface-item rounded-lg space-y-2">
          <p className="text-[10px] text-app-secondary font-medium">Add New Wish</p>
          <div className="grid grid-cols-3 gap-2">
            <select value={editForm.type} onChange={e => setEditForm({...editForm, type: e.target.value})}
              className="col-span-3 bg-surface-input border border-input rounded px-2 py-1 text-[10px] text-app">
              <option value="">Type...</option>
              <option value="org">Organisation</option>
              <option value="deal">Deal</option>
              <option value="people">Person</option>
              <option value="job">Job</option>
            </select>
            <input value={editForm.title} onChange={e => setEditForm({...editForm, title: e.target.value})}
              placeholder="Title..." className="col-span-3 bg-surface-input border border-input rounded px-2 py-1 text-[10px] text-app" />
            <textarea value={editForm.description} onChange={e => setEditForm({...editForm, description: e.target.value})}
              placeholder="Description (optional)..." rows={2} className="col-span-3 bg-surface-input border border-input rounded px-2 py-1 text-[10px] text-app" />
            <button onClick={addWish} className="col-span-3 bg-rose-600 hover:bg-rose-500 text-white text-[10px] py-1 rounded font-medium">Add Wish</button>
          </div>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-app-secondary" /></div>
      ) : (
        <div className="space-y-2">
          {sections.map(section => (
            <div key={section.key}>
              <button onClick={() => setExpanded(expanded === section.key ? null : section.key)}
                className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs font-medium ${section.bg} ${section.text} transition-colors`}>
                <section.icon className="w-3.5 h-3.5" />
                <span className="flex-1 text-left">{section.label}</span>
                <span className="text-[10px] opacity-70">({section.items.length})</span>
                {expanded === section.key ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              {expanded === section.key && (
                <div className="mt-1 space-y-1 max-h-[180px] overflow-y-auto pl-3">
                  {section.items.length === 0 ? (
                    <p className="text-[10px] text-app-secondary py-1 italic">No items listed</p>
                  ) : (
                    section.items.map((item, i) => (
                      <div key={i} className="bg-surface-item rounded-lg p-2 space-y-1 relative">
                        {editing && (
                          <button onClick={() => removeWish(item)}
                            className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] shadow-lg hover:bg-red-500 transition-colors">
                            ×
                          </button>
                        )}
                        <p className="text-[11px] font-medium">{item.title}</p>
                        {item.description && <p className="text-[9px] text-app-secondary line-clamp-2">{item.description}</p>}
                        <div className="flex items-center gap-2 pt-1">
                          {item.target_id && section.key === "people" && (
                            <button onClick={() => onNavigate(`/people/${item.target_id}`)}
                              className="text-[9px] text-emerald-400 hover:text-emerald-300 flex items-center gap-1">
                              <UserPlus className="w-2.5 h-2.5" /> Follow
                            </button>
                          )}
                          {item.target_id && section.key === "orgs" && (
                            <button onClick={() => onNavigate(`/corporate-hub/${item.target_id}`)}
                              className="text-[9px] text-violet-400 hover:text-violet-300 flex items-center gap-1">
                              <ExternalLink className="w-2.5 h-2.5" /> View
                            </button>
                          )}
                          {section.key === "jobs" && (
                            <button onClick={() => onNavigate("/comms-centre/mail")}
                              className="text-[9px] text-blue-400 hover:text-blue-300 flex items-center gap-1">
                              <Mail className="w-2.5 h-2.5" /> Send Details
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── BUSINESS CARD ──
function BusinessCard({ user, followers, following }) {
  const [showFollowers, setShowFollowers] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [followerList, setFollowerList] = useState([]);
  const [followingList, setFollowingList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  const avatar = user ? dummyAvatar(user.name, user.user_id) : { bg: "", letter: "?" };

  const fetchFollowers = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/user/followers`, { headers: getHeaders() });
      setFollowerList(res.data.items || []);
    } catch { setFollowerList([]); }
    setLoadingList(false);
    setShowFollowers(true);
    setShowFollowing(false);
  };

  const fetchFollowing = async () => {
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/user/following`, { headers: getHeaders() });
      setFollowingList(res.data.items || []);
    } catch { setFollowingList([]); }
    setLoadingList(false);
    setShowFollowing(true);
    setShowFollowers(false);
  };

  // Inline editing useEffect + save
  useEffect(() => {
    if (user && !editing) {
      setEditForm({
        name: user.name || "", position: user.position || "", profession: user.profession || "",
        country: user.country || "", city: user.city || user.location || "", phone: user.phone || "",
        skills: (user.skills || []).join(", "), services: (user.services || []).join(", "),
      });
    }
  }, [user, editing]);

  const saveProfile = async () => {
    try {
      const payload = {
        name: editForm.name, position: editForm.position, profession: editForm.profession,
        country: editForm.country, city: editForm.city, phone: editForm.phone,
        skills: editForm.skills.split(",").map(s => s.trim()).filter(Boolean),
        services: editForm.services.split(",").map(s => s.trim()).filter(Boolean),
      };
      await axios.put(`${API}/auth/profile`, payload, { headers: getHeaders() });
      toast.success("Profile saved!"); setEditing(false); window.location.reload();
    } catch { toast.error("Failed to save"); }
  };

  if (editing) {
    return (
      <div className="h-full">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5">
          <Edit3 className="w-3.5 h-3.5 text-violet-400" /> Edit Profile
        </h3>
        <div className="space-y-2 text-xs">
          {["name","position","profession","country","city","phone"].map(f => (
            <div key={f} className="space-y-0.5">
              <label className="text-[10px] text-app-secondary uppercase">{f}</label>
              <input value={editForm[f] || ""} onChange={e => setEditForm({...editForm, [f]: e.target.value})}
                className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
            </div>
          ))}
          <div className="space-y-0.5">
            <label className="text-[10px] text-app-secondary uppercase">Skills (comma separated)</label>
            <input value={editForm.skills || ""} onChange={e => setEditForm({...editForm, skills: e.target.value})}
              className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
          </div>
          <div className="space-y-0.5">
            <label className="text-[10px] text-app-secondary uppercase">Services (comma separated)</label>
            <input value={editForm.services || ""} onChange={e => setEditForm({...editForm, services: e.target.value})}
              className="w-full bg-surface-item border border-input rounded px-2 py-1 text-xs text-app focus:outline-none focus:border-violet-500" />
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={saveProfile} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs py-2 rounded-lg font-medium">Save</button>
          <button onClick={() => setEditing(false)} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white text-xs py-2 rounded-lg">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Top: Avatar + Name + Action Row */}
      <div className="flex items-center gap-3 mb-3">
        <Avatar className="w-16 h-16 border-2 border-violet-500/30 shrink-0">
          {user.picture ? <AvatarImage src={user.picture} className="object-cover" /> : (
            <AvatarFallback className={`bg-gradient-to-br ${avatar.bg} text-xl font-bold text-white`}>
              {avatar.letter}
            </AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold">{user.name || "Your Name"}</h1>
          <p className="text-xs text-app-secondary">{user.position || user.profession || ""}</p>
          <p className="text-[10px] text-app-secondary">@{user.username || "user"}</p>
        </div>
      </div>

      {/* Compact side-by-side: Position,Profession,Phone,Email on left / From,Location,Skills,Services,F counts on right */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs mb-3">
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">Position</span><p className="text-app">{user.position || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Profession</span><p className="text-app">{user.profession || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Phone</span><p className="text-app">{user.phone || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Email</span><p className="text-app">{user.email || "N/A"}</p></div>
        </div>
        <div className="space-y-1">
          <div><span className="text-[9px] text-app-secondary uppercase">From</span><p className="text-app">{user.country || "N/A"}</p></div>
          <div><span className="text-[9px] text-app-secondary uppercase">Location</span><p className="text-app">{user.city || user.country || "N/A"}</p></div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Skills</h4>
            <div className="flex flex-wrap gap-1">{user.skills && Array.isArray(user.skills) && user.skills.length > 0 ? user.skills.map((s, i) => (<Badge key={i} className="bg-emerald-600/20 text-emerald-400 text-[8px] px-1.5 py-0.5">{s}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
          <div>
            <h4 className="text-[9px] uppercase tracking-wider text-app-secondary mb-0.5">Services I Can Offer</h4>
            <div className="flex flex-wrap gap-1">{user.services && Array.isArray(user.services) && user.services.length > 0 ? user.services.map((s, i) => (<Badge key={i} className="bg-amber-600/20 text-amber-400 text-[8px] px-1.5 py-0.5">{s}</Badge>)) : (<span className="text-[10px] text-app-secondary">None</span>)}</div>
          </div>
          <button onClick={fetchFollowers} className="text-[10px] text-app-secondary hover:text-app mt-1"><strong className="text-app">{followers}</strong> Followers</button>
          <button onClick={fetchFollowing} className="text-[10px] text-app-secondary hover:text-app"><strong className="text-app">{following}</strong> Following</button>
        </div>
      </div>

      {/* Action Buttons Row */}
      <div className="flex flex-wrap gap-2 pt-3 border-t border-white/10">
        <button onClick={() => setEditing(true)}
          className="flex-1 min-w-[100px] bg-violet-600 hover:bg-violet-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors">
          <Edit3 className="w-3 h-3" /> Edit Profile
        </button>
        <button onClick={() => window.location.href="/comms-centre/mail/inbox"}
          className="flex-1 min-w-[100px] bg-blue-600 hover:bg-blue-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors">
          <Mail className="w-3 h-3" /> Request Contact
        </button>
        <button onClick={() => window.location.href="/comms-centre/chat"}
          className="flex-1 min-w-[100px] bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors">
          <Share2 className="w-3 h-3" /> Share Contact
        </button>
        <button onClick={() => window.location.href="/comms-centre/chat"}
          className="flex-1 min-w-[100px] bg-amber-600 hover:bg-amber-500 text-white text-[10px] font-medium py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition-colors">
          <UserPlus className="w-3 h-3" /> Add to Group
        </button>
      </div>

      {/* Followers/Following popup list */}
      {showFollowers && (
            <div className="mt-2 p-2 bg-surface-item rounded-lg max-h-[150px] overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-app-secondary font-medium">Followers</p>
                <button onClick={() => setShowFollowers(false)} className="text-app-secondary hover:text-app text-[9px]">Close</button>
              </div>
              {loadingList ? (
                <Loader2 className="w-3 h-3 animate-spin text-app-secondary mx-auto" />
              ) : followerList.length === 0 ? (
                <p className="text-[10px] text-app-secondary">No followers yet</p>
              ) : (
                followerList.map(f => (
                  <div key={f.user_id} className="flex items-center gap-2 py-1">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${dummyAvatar(f.name, f.user_id).bg} flex items-center justify-center text-white text-[8px] font-bold`}>
                      {dummyAvatar(f.name, f.user_id).letter}
                    </div>
                    <span className="text-[10px] text-zinc-300">{f.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
          {showFollowing && (
            <div className="mt-2 p-2 bg-surface-item rounded-lg max-h-[150px] overflow-y-auto">
              <div className="flex items-center justify-between mb-1">
                <p className="text-[10px] text-app-secondary font-medium">Following</p>
                <button onClick={() => setShowFollowing(false)} className="text-app-secondary hover:text-app text-[9px]">Close</button>
              </div>
              {loadingList ? (
                <Loader2 className="w-3 h-3 animate-spin text-app-secondary mx-auto" />
              ) : followingList.length === 0 ? (
                <p className="text-[10px] text-app-secondary">Not following anyone yet</p>
              ) : (
                followingList.map(f => (
                  <div key={f.user_id} className="flex items-center gap-2 py-1">
                    <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${dummyAvatar(f.name, f.user_id).bg} flex items-center justify-center text-white text-[8px] font-bold`}>
                      {dummyAvatar(f.name, f.user_id).letter}
                    </div>
                    <span className="text-[10px] text-zinc-300">{f.name}</span>
                  </div>
                ))
              )}
            </div>
          )}
    
    </div>
  );
}

// ── SKILLS + ACTIONS BLOCK (Middle column) ──
// ── MAIN HOME PAGE ──



export default function AllBaiseProfile() {
  const auth = useAuth();
  const navigate = useNavigate();
  const user = auth.user;

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postContent, setPostContent] = useState("");
  const [posting, setPosting] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});
  const [reactions, setReactions] = useState({});
  const [shares, setShares] = useState({});
  const [showReactionPicker, setShowReactionPicker] = useState({});
  const [postImage, setPostImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [commentInputs, setCommentInputs] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [commentsData, setCommentsData] = useState({});
  const [followerCount, setFollowerCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [fullProfile, setFullProfile] = useState(null);
  const [showMobileSidebars, setShowMobileSidebars] = useState(window.innerWidth >= 1024);

  const avatar = user ? dummyAvatar(user.name, user.user_id) : { bg: "", letter: "?" };

  // Fetch full profile data (position, skills, hobbies, services, wishes, etc.)
    useEffect(() => {
    const handleResize = () => { if (window.innerWidth >= 1024) setShowMobileSidebars(true); };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

useEffect(() => {
    if (!user?.user_id) return;
    axios.get(`${API}/v2/people/${user.user_id}`, { headers: getHeaders() })
      .then(res => {
        if (!res.data?.error) setFullProfile(res.data);
      })
      .catch(() => {});
  }, [user?.user_id]);

  // Merge auth user with full profile data for BusinessCard
  const profileData = fullProfile ? { ...user, ...fullProfile } : user;

  // Fetch follower/following counts
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/user/followers`, { headers: getHeaders() })
      .then(res => setFollowerCount(res.data.total || 0))
      .catch(() => {});
    axios.get(`${API}/user/following`, { headers: getHeaders() })
      .then(res => setFollowingCount(res.data.total || 0))
      .catch(() => {});
  }, [user]);

  const fetchPosts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/activity/${user.user_id}`, { headers: getHeaders() });
      const items = res.data.items || [];
      setPosts(items);
      // Fetch reactions and shares for each post
      items.forEach(p => {
        fetchReactions(p.id);
        setShares(prev => ({ ...prev, [p.id]: p.shares || 0 }));
      });
    } catch { setPosts([]); }
    setLoading(false);
  }, [user]);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const handlePost = async () => {
    if (!postContent.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API}/activity`, { content: postContent }, { headers: getHeaders() });
      setPosts(prev => [res.data, ...prev]);
      setPostContent("");
      toast.success("Posted!");
    } catch { toast.error("Failed to post"); }
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/like`, {}, { headers: getHeaders() });
      setPosts(prev => prev.map(p => {
        if (p.id === postId) return { ...p, likes: p.likes + (res.data.status === "liked" ? 1 : -1) };
        return p;
      }));
      setLikedPosts(prev => ({ ...prev, [postId]: res.data.status === "liked" }));
    } catch {}
  };

  const handleReact = async (postId, reaction) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/react`, { reaction }, { headers: getHeaders() });
      if (res.data.status === "removed") {
        setReactions(prev => ({ ...prev, [postId]: { ...prev[postId], [reaction]: Math.max(0, (prev[postId]?.[reaction] || 0) - 1) } }));
      } else {
        setReactions(prev => ({ ...prev, [postId]: { ...prev[postId], [reaction]: (prev[postId]?.[reaction] || 0) + 1 } }));
      }
      setShowReactionPicker(prev => ({ ...prev, [postId]: false }));
    } catch {}
  };

  const handleShare = async (postId) => {
    try {
      const res = await axios.post(`${API}/activity/${postId}/share`, {}, { headers: getHeaders() });
      setShares(prev => ({ ...prev, [postId]: res.data.shares }));
      toast.success("Post shared!");
    } catch { toast.error("Failed to share"); }
  };

  const handleUploadImage = async (postId) => {
    if (!postImage) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", postImage);
      await axios.post(`${API}/activity/${postId}/image`, formData, { headers: { ...getHeaders(), "Content-Type": "multipart/form-data" } });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, image_url: URL.createObjectURL(postImage) } : p));
      setPostImage(null);
      toast.success("Image uploaded!");
    } catch { toast.error("Failed to upload image"); }
    setUploading(false);
  };

  const fetchReactions = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/reactions`, { headers: getHeaders() });
      setReactions(prev => ({ ...prev, [postId]: res.data.reactions }));
    } catch {}
  };

  const totalReactions = (postId) => {
    const r = reactions[postId] || {};
    return Object.values(r).reduce((a, b) => a + b, 0);
  };

  const REACTION_EMOJIS = { "like": "👍", "love": "❤️", "celebrate": "🎉", "support": "💪", "insightful": "💡" };
  const REACTION_COLORS = { "like": "text-blue-400", "love": "text-red-400", "celebrate": "text-amber-400", "support": "text-emerald-400", "insightful": "text-purple-400" };

  const fetchComments = async (postId) => {
    try {
      const res = await axios.get(`${API}/activity/${postId}/comments`, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: res.data.items || [] }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
    } catch {}
  };

  const handleComment = async (postId) => {
    const text = commentInputs[postId];
    if (!text?.trim()) return;
    try {
      const res = await axios.post(`${API}/activity/${postId}/comments`, { content: text }, { headers: getHeaders() });
      setCommentsData(prev => ({ ...prev, [postId]: [...(prev[postId] || []), res.data] }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: (p.comments_count || 0) + 1 } : p));
      setCommentInputs(prev => ({ ...prev, [postId]: "" }));
    } catch { toast.error("Failed to comment"); }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 1000);
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString();
  };

  if (!user) {
    return <div className="min-h-screen bg-app flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-app-secondary" /></div>;
  }

  return (
    <div className="min-h-screen bg-app text-app">
      <TopNav />
      <div className="max-w-7xl mx-auto px-3 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">

          {/* ── TOP ROW: Profile (9 cols) + Wishlist (3 cols) ── */}
          <div className="lg:col-span-9">
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <BusinessCard user={profileData} followers={followerCount} following={followingCount} />
            </Card>
          </div>

          <div className="lg:col-span-3">
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <WishlistMenu onNavigate={navigate} isOwner={true} />
            </Card>
          </div>

          {/* ── LEFT COLUMN: Corporate Hub + People ── */}
          {/* Mobile sidebar toggle */}
          <div className="lg:hidden col-span-full flex gap-2 mb-2">
            <button onClick={() => setShowMobileSidebars(!showMobileSidebars)}
              className="flex-1 bg-surface-item hover:bg-surface-hover text-zinc-300 text-xs py-2 rounded-lg font-medium transition-colors">
              {showMobileSidebars ? "Hide Sidebars ▲" : "Show Sidebars ▼"}
            </button>
          </div>
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <CorporateSidebar onNavigate={navigate} />
            </Card>
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <PeopleSidebar onNavigate={navigate} />
            </Card>
          </div>
          )}

          {/* ── CENTER COLUMN: Activity Feed ── */}
          <div className="lg:col-span-6 space-y-4">
            {/* Post Composer */}
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4">
              <div className="flex items-start gap-3">
                <Avatar className="w-9 h-9 shrink-0">
                  <AvatarFallback className={`bg-gradient-to-br ${avatar.bg} text-xs font-bold text-white`}>
                    {avatar.letter}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <textarea value={postContent} onChange={(e) => setPostContent(e.target.value)}
                    placeholder="What do you want to share?"
                    className="w-full bg-transparent border-0 text-sm text-app placeholder:text-app-secondary resize-none focus:outline-none min-h-[60px]" rows={2} />
                  <div className="flex items-center justify-between pt-2 border-t border-white/10 dark:border-white/10 light:border-gray-200">
                    <div className="flex items-center gap-2">
                      <input type="file" accept="image/*" ref={fileInputRef} className="hidden" onChange={e => setPostImage(e.target.files?.[0] || null)} />
                      <button onClick={() => fileInputRef.current?.click()} className="text-app-secondary hover:text-app transition-colors p-1" title="Attach image">
                        <Image className="w-4 h-4" />
                      </button>
                      {postImage && (
                        <div className="flex items-center gap-1">
                          <span className="text-[10px] text-app-secondary truncate max-w-[80px]">{postImage.name}</span>
                          <button onClick={() => setPostImage(null)} className="text-red-400 hover:text-red-300 text-[10px]">✕</button>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {uploading && <Loader2 className="w-3 h-3 animate-spin text-app-secondary" />}
                      <Button onClick={handlePost} disabled={posting || !postContent.trim()}
                        className="bg-violet-600 hover:bg-violet-500 text-white rounded-lg text-xs h-8 px-4">
                        {posting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Send className="w-3 h-3 mr-1" />} Post
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Activity Feed */}
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-app-secondary" /></div>
            ) : posts.length === 0 ? (
              <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-6 text-center">
                <p className="text-sm text-app-secondary">No posts yet. Share something with your network!</p>
              </Card>
            ) : (
              posts.slice(0, 20).map(post => (
                <Card key={post.id} className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Avatar className="w-9 h-9">
                      <AvatarFallback className={`bg-gradient-to-br ${dummyAvatar(post.author_name, post.id).bg} text-xs font-bold text-white`}>
                        {(post.author_name || "U")[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{post.author_name || "User"}</p>
                      <p className="text-[10px] text-app-secondary">{formatTime(post.created_at)}</p>
                    </div>
                  </div>
                  <p className="text-sm text-app mb-3 whitespace-pre-wrap">{post.content}</p>
                  <div className="flex items-center gap-3 text-xs text-app-secondary border-t border-white/10 dark:border-white/10 light:border-gray-200 pt-3 flex-wrap">
                    {/* Reaction Picker */}
                    <div className="relative">
                      <button onClick={() => setShowReactionPicker(prev => ({ ...prev, [post.id]: !prev[post.id] }))}
                        className="flex items-center gap-1 hover:text-amber-400 transition">
                        <Heart className={`w-4 h-4 ${likedPosts[post.id] || totalReactions(post.id) > 0 ? "fill-red-400 text-red-400" : ""}`} />
                        {totalReactions(post.id) || 0}
                      </button>
                      {showReactionPicker[post.id] && (
                        <div className="absolute bottom-full left-0 mb-1 flex gap-1 bg-zinc-800 border border-input rounded-lg px-2 py-1.5 shadow-xl z-10">
                          {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => (
                            <button key={key} onClick={() => handleReact(post.id, key)}
                              className="hover:scale-125 transition-transform text-sm p-0.5" title={key}>{emoji}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Like button (also toggles like reaction) */}
                    <button onClick={() => handleReact(post.id, "like")}
                      className={`flex items-center gap-1 hover:text-red-400 transition ${likedPosts[post.id] ? "text-red-400" : ""}`}>
                      <Heart className={`w-4 h-4 ${likedPosts[post.id] ? "fill-red-400" : ""}`} />
                      {post.likes || 0}
                    </button>
                    {/* Share */}
                    <button onClick={() => handleShare(post.id)}
                      className="flex items-center gap-1 hover:text-emerald-400 transition">
                      <Share2 className="w-4 h-4" />
                      {shares[post.id] || 0}
                    </button>
                    {/* Comments */}
                    <button onClick={() => {
                      if (expandedComments[post.id]) setExpandedComments(prev => ({ ...prev, [post.id]: false }));
                      else fetchComments(post.id);
                    }} className="flex items-center gap-1 hover:text-blue-400 transition">
                      <MessageCircle className="w-4 h-4" />
                      {post.comments_count || 0}
                    </button>
                  </div>
                  {/* Show reaction emoji counts */}
                  {totalReactions(post.id) > 0 && (
                    <div className="flex items-center gap-1.5 pt-1 text-xs">
                      {Object.entries(REACTION_EMOJIS).map(([key, emoji]) => {
                        const count = reactions[post.id]?.[key] || 0;
                        if (count === 0) return null;
                        return (
                          <span key={key} className="flex items-center gap-0.5 text-app-secondary">
                            <span>{emoji}</span>
                            <span className="text-[10px]">{count}</span>
                          </span>
                        );
                      })}
                    </div>
                  )}
                  {post.image_url && (
                    <div className="mt-2 rounded-lg overflow-hidden">
                      <img src={post.image_url} alt="Post attachment" className="w-full max-h-64 object-cover rounded-lg" onError={e => e.target.style.display = "none"} />
                    </div>
                  )}
                  {expandedComments[post.id] && (
                    <div className="mt-3 pt-3 border-t border-white/10 dark:border-white/10 light:border-gray-200 space-y-3">
                      {commentsData[post.id]?.map(c => (
                        <div key={c.id} className="flex items-start gap-2">
                          <Avatar className="w-6 h-6 shrink-0">
                            <AvatarFallback className="bg-zinc-700 text-[10px] text-white">{(c.author_name || "U")[0]}</AvatarFallback>
                          </Avatar>
                          <div className="flex-1 bg-surface-item dark:bg-surface-item light:bg-gray-100 rounded-lg px-3 py-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium">{c.author_name}</span>
                              <span className="text-[10px] text-app-secondary">{formatTime(c.created_at)}</span>
                            </div>
                            <p className="text-xs text-app mt-0.5">{c.content}</p>
                          </div>
                        </div>
                      ))}
                      <div className="flex items-center gap-2">
                        <input type="text" value={commentInputs[post.id] || ""}
                          onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === "Enter" && handleComment(post.id)}
                          placeholder="Write a comment..."
                          className="flex-1 bg-surface-item dark:bg-surface-item light:bg-gray-100 border border-input dark:border-input light:border-gray-300 rounded-lg px-3 py-1.5 text-sm text-app placeholder:text-app-secondary focus:outline-none focus:border-violet-500" />
                        <button onClick={() => handleComment(post.id)} className="text-violet-400 hover:text-violet-300">
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>

          {/* ── RIGHT: Deals + Wishes ── */}
          {showMobileSidebars && (
          <div className="lg:col-span-3 space-y-3">
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <DealsSidebar onNavigate={navigate} />
            </Card>
            <Card className="bg-app-card dark:bg-app-card light:bg-white border-white/10 dark:border-white/10 light:border-gray-200 p-3">
              <WishesSidebar onNavigate={navigate} />
            </Card>
          </div>
          )}

        </div>
      </div>
    </div>
  );
}
